import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getSupabaseClient } from '@/lib/supabase'
import { getSupabaseUrl } from '@/lib/runtime-env'
import type { ProductRole } from '@dcp/shared'

/**
 * Status of an organization invitation.
 *
 * 'processing' is a transient claim state set by the edge function during
 * accept (between the atomic claim and the final 'accepted' commit). End
 * users normally never see it — rows leave 'processing' within milliseconds.
 * Admin UI may briefly observe it for genuinely-in-flight rows, or for rows
 * stuck after an edge-function crash (recovered manually via SQL — see
 * migration 20260506000001).
 */
export type InvitationStatus = 'open' | 'processing' | 'accepted' | 'expired' | 'cancelled'

/**
 * Type of invitation
 */
export type InviteType = 'organization' | 'admin' | 'user'

/** Invite type for team-member (product-role) invitations. */
export const USER_INVITE_TYPE: InviteType = 'user'

/**
 * Organization invitation data structure
 */
export interface OrganizationInvitation {
  id: string
  organization_name: string
  admin_email: string
  admin_first_name: string | null
  admin_last_name: string | null
  status: InvitationStatus
  invite_type: InviteType
  invitation_token: string
  expires_at: string
  invited_by: string
  accepted_at: string | null
  created_organization_id: string | null
  created_at: string
  updated_at: string
  // For user invitations - target organization
  target_organization_id: string | null
  /**
   * Feature-level role assigned at invite time (user invitations only).
   * NULL for legacy organization/admin invite_types which predate the product_role feature.
   */
  product_role: string | null
  /** Bumped each resend; DCP-139 idempotency key version. */
  resend_count: number
  /**
   * Whether the invited address already has an account (DCP-350). Returned by
   * `get-by-token` only, so the accept page can ask an existing user to SIGN IN
   * and confirm rather than offering to create an account that cannot be
   * created — Supabase answers 422 email_exists for a taken address.
   *
   * Optional because every other endpoint returns the plain invitation row.
   */
  user_exists?: boolean
}

/**
 * Parameters for creating a new invitation
 */
export interface CreateInvitationParams {
  organization_name: string
  admin_email: string
  admin_first_name?: string
  admin_last_name?: string
}

/**
 * Parameters for creating an admin invitation (multi-org access)
 */
export interface CreateAdminInvitationParams {
  admin_email: string
  admin_first_name?: string
  admin_last_name?: string
  assigned_organization_ids: string[]
}

/**
 * Parameters for creating a user invitation (single org, product_role required).
 * product_role determines the feature-level permissions granted inside the tenant;
 * it is forwarded verbatim to the create-user edge function action.
 */
export interface CreateUserInvitationParams {
  admin_email: string
  admin_first_name?: string
  admin_last_name?: string
  target_organization_id: string
  /** Feature-level role assigned to the invited user within the organization. */
  product_role: ProductRole
}

/**
 * Parameters for accepting an invitation
 */
export interface AcceptInvitationParams {
  token: string
  password: string
}

/**
 * Organization data with admin info
 */
export interface Organization {
  id: string
  name: string
  status: 'active' | 'suspended'
  suspended_at: string | null
  created_at: string
  updated_at: string
  admin_email: string | null
  admin_name: string | null
}

/**
 * Safely parses JSON from a response, returning null if parsing fails
 * Handles cases where the server returns non-JSON responses (HTML error pages, etc.)
 */
async function safeParseJson(response: Response): Promise<unknown> {
  try {
    const text = await response.text()
    return text ? JSON.parse(text) : null
  } catch {
    return null
  }
}

/**
 * Extracts error message from a parsed JSON response or returns default
 */
function getErrorMessage(json: unknown, defaultMessage: string): string {
  if (json && typeof json === 'object' && 'error' in json) {
    return String((json as { error: unknown }).error) || defaultMessage
  }
  return defaultMessage
}

/**
 * Query keys for invitation-related data
 */
const INVITATION_KEYS = {
  all: ['invitations'] as const,
  list: (status?: InvitationStatus) => ['invitations', 'list', status] as const,
  byToken: (token: string) => ['invitations', 'token', token] as const,
  organizations: ['organizations'] as const,
}

/** Root segment of the unified team-invitations cache key. */
const UNIFIED_TEAM_INVITATIONS_KEY = 'unified-team-invitations'

/**
 * Query keys for the unified team-member list's invitation slice
 * (the cache read by useOrgInvitationsAll in useTeamMembers).
 *
 * Centralized here so the invitation mutations below and the reader share a
 * single source of truth — query-key drift between writer and reader is exactly
 * what caused DCP-236.
 *
 * `all` is the broad root key: React Query prefix-matches it against every
 * org's scoped `invitations(orgId)` entry, so one invalidate refreshes the
 * active org's table without each mutation needing to know the orgId.
 */
export const UNIFIED_TEAM_INVITATIONS_KEYS = {
  all: [UNIFIED_TEAM_INVITATIONS_KEY] as const,
  invitations: (orgId?: string) => [UNIFIED_TEAM_INVITATIONS_KEY, orgId] as const,
}

/**
 * Hook to fetch all invitations with optional status filter
 * @param status - Optional filter by invitation status (open, accepted, expired)
 */
export function useInvitations(status?: InvitationStatus) {
  return useQuery({
    queryKey: INVITATION_KEYS.list(status),
    queryFn: async () => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'list',
            ...(status && { status }),
          }),
        }
      )

      if (!response.ok) {
        const errorJson = await safeParseJson(response)
        throw new Error(getErrorMessage(errorJson, 'Failed to fetch invitations'))
      }

      const result = await safeParseJson(response)
      if (!result || typeof result !== 'object' || !('data' in result)) {
        throw new Error('Invalid response format from server')
      }
      return (result as { data: OrganizationInvitation[] }).data
    },
  })
}

/**
 * Hook to fetch an invitation by token
 * Used on the accept invitation page to display invitation details
 * @param token - Invitation token from URL
 */
export function useInvitationByToken(token: string) {
  return useQuery({
    queryKey: INVITATION_KEYS.byToken(token),
    queryFn: async () => {
      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'get-by-token',
            token,
          }),
        }
      )

      if (!response.ok) {
        const errorJson = await safeParseJson(response)
        throw new Error(getErrorMessage(errorJson, 'Failed to fetch invitation'))
      }

      const result = await safeParseJson(response)
      if (!result || typeof result !== 'object' || !('data' in result)) {
        throw new Error('Invalid response format from server')
      }
      return (result as { data: OrganizationInvitation }).data
    },
    enabled: !!token,
  })
}

/**
 * Hook to create a new organization invitation
 * Sends an invitation email to the specified admin user
 */
export function useCreateInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: CreateInvitationParams) => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'create',
            ...params,
          }),
        }
      )

      if (!response.ok) {
        const errorJson = await safeParseJson(response)
        throw new Error(getErrorMessage(errorJson, 'Failed to create invitation'))
      }

      return safeParseJson(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.all })
    },
  })
}

/**
 * Hook to create an admin invitation
 * Sends an invitation email to an admin with access to specified organizations
 */
export function useCreateAdminInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: CreateAdminInvitationParams) => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'create-admin',
            ...params,
          }),
        }
      )

      if (!response.ok) {
        const errorJson = await safeParseJson(response)
        throw new Error(getErrorMessage(errorJson, 'Failed to create admin invitation'))
      }

      return safeParseJson(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.all })
    },
  })
}

/**
 * Hook to create a user invitation for a specific organization
 * Creates a tech_team user bound to the target organization
 */
export function useCreateUserInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: CreateUserInvitationParams) => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'create-user',
            ...params,
          }),
        }
      )

      if (!response.ok) {
        const errorJson = await safeParseJson(response)
        throw new Error(getErrorMessage(errorJson, 'Failed to create user invitation'))
      }

      return safeParseJson(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.all })
      queryClient.invalidateQueries({ queryKey: UNIFIED_TEAM_INVITATIONS_KEYS.all })
    },
  })
}

/**
 * Hook to resend an existing invitation
 * Sends a new invitation email with the same token
 * @param invitation_id - ID of the invitation to resend
 */
export function useResendInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitation_id: string) => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'resend',
            invitation_id,
          }),
        }
      )

      if (!response.ok) {
        const errorJson = await safeParseJson(response)
        throw new Error(getErrorMessage(errorJson, 'Failed to resend invitation'))
      }

      return safeParseJson(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.all })
      queryClient.invalidateQueries({ queryKey: UNIFIED_TEAM_INVITATIONS_KEYS.all })
    },
  })
}

/**
 * Hook to cancel an open invitation
 * Sets the invitation status to 'cancelled'
 * @param invitation_id - ID of the invitation to cancel
 */
export function useCancelInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitation_id: string) => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'cancel',
            invitation_id,
          }),
        }
      )

      if (!response.ok) {
        const errorJson = await safeParseJson(response)
        throw new Error(getErrorMessage(errorJson, 'Failed to cancel invitation'))
      }

      return safeParseJson(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.all })
      queryClient.invalidateQueries({ queryKey: UNIFIED_TEAM_INVITATIONS_KEYS.all })
    },
  })
}

/**
 * Hook to accept an organization invitation
 * Creates a new user account and organization, marks invitation as accepted
 * This is a public endpoint that doesn't require authentication
 */
export function useAcceptInvitation() {
  return useMutation({
    mutationFn: async (params: AcceptInvitationParams) => {
      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'accept',
            token: params.token,
            password: params.password,
          }),
        }
      )

      if (!response.ok) {
        const errorJson = await safeParseJson(response)
        throw new Error(getErrorMessage(errorJson, 'Failed to accept invitation'))
      }

      return safeParseJson(response)
    },
  })
}

/**
 * Accept an invitation with an account the user ALREADY has (DCP-350).
 *
 * Separate from {@link useAcceptInvitation} because the two are different
 * operations, not two shapes of one. The ordinary path is anonymous and sends a
 * password — it CREATES the account. This one sends the session token and no
 * password: the account exists, and proving ownership of it is exactly what
 * stops a leaked invitation link from attaching an organization to somebody
 * else's account. The server rejects the call unless the signed-in user is the
 * invited address.
 *
 * The caller must therefore be signed in before this runs; the accept page
 * signs them in first, then calls this.
 */
export function useAcceptInvitationAsExistingUser() {
  return useMutation({
    mutationFn: async (params: { token: string }) => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Please sign in to accept this invitation')
      }

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'accept-existing',
            token: params.token,
          }),
        }
      )

      if (!response.ok) {
        const errorJson = await safeParseJson(response)
        throw new Error(getErrorMessage(errorJson, 'Failed to accept invitation'))
      }

      return safeParseJson(response)
    },
  })
}

/**
 * Hook to fetch all organizations (admin only)
 */
export function useOrganizations() {
  return useQuery({
    queryKey: INVITATION_KEYS.organizations,
    queryFn: async () => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'list-organizations',
          }),
        }
      )

      if (!response.ok) {
        const errorJson = await safeParseJson(response)
        throw new Error(getErrorMessage(errorJson, 'Failed to fetch organizations'))
      }

      const result = await safeParseJson(response)
      if (!result || typeof result !== 'object' || !('data' in result)) {
        throw new Error('Invalid response format from server')
      }
      return (result as { data: Organization[] }).data
    },
  })
}